import { Component, HostListener, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { TableColumnTypeEnum, TableFilterTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { MetricsService } from 'src/app/shared/services/metrics.service';
import { Scenario } from '../scenarios/models';
import { ScenariosService } from '../scenarios/scenarios.service';

@Component({
  selector: 'app-quick-replies',
  templateUrl: './quick-replies.component.html',
  styleUrls: ['./quick-replies.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class QuickRepliesComponent implements OnDestroy {
  selectedTabIndex = 0;
  scenarioFlavor: 'quick-replies' | 'campaign-scenarios';
  config: TableConfig = {
    displayedColumns: ['account', 'name', 'activated', 'modifier', 'editionDate', 'creator', 'creationDate', 'actions'],
    columns: [
      {
        name: 'account',
        translationKey: 'account',
        property: 'site',
        type: TableColumnTypeEnum.SITE,
        sortEnabled: true,
      },
      {
        name: 'name',
        translationKey: 'parameters/quick_replies/scenario_name_column',
        property: 'scenarioNameLink',
        type: TableColumnTypeEnum.LINK,
        sortEnabled: true,
      },
      {
        name: 'activated',
        translationKey: 'parameters/quick_replies/scenario_active_column',
        property: 'is_active',
        type: TableColumnTypeEnum.ACTIVE,
        sortEnabled: true,
      },
      { name: 'creator', translationKey: 'parameters/scenarios/creator', property: 'createdByEmail' },
      {
        name: 'creationDate',
        translationKey: 'parameters/scenarios/creation_date',
        property: 'creation_date',
        type: TableColumnTypeEnum.DATE,
        sortEnabled: true,
      },
      { name: 'modifier', translationKey: 'parameters/scenarios/last_editor', property: 'lastModifiedByEmail' },
      {
        name: 'editionDate',
        translationKey: 'parameters/scenarios/last_modification_date',
        property: 'last_modification_date',
        type: TableColumnTypeEnum.DATE,
        sortEnabled: true,
      },
    ],
    actions: [
      {
        clickFn: (element: Scenario) => this.duplicate(element),
        icon: 'content_copy',
      },
      {
        tooltip: 'delete',
        tooltipDisabled: 'quick-replies/delete-disabled',
        customClass: () => (!this.isAlcmeonUser ? 'hidden' : ''),
        isDisabled: element => element.is_active,
        clickFn: (element: Scenario) => this.delete(element),
        icon: 'delete',
        color: 'warn',
      },
    ],
  };
  filters: TableFilters = {
    activated: {
      name: 'activated',
      items: [
        { id: 1, label: this.basicService.translate('scenarios/list/active') },
        { id: 0, label: this.basicService.translate('scenarios/list/inactive') },
      ],
      placeholder: null,
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
    creationDate: {
      name: 'creationDateRange',
      placeholder: 'engagement/history/filters/date-sent',
      type: TableFilterTypeEnum.DATE,
    },
    editionDate: {
      name: 'editionDateRange',
      placeholder: 'engagement/history/filters/date-sent',
      type: TableFilterTypeEnum.DATE,
    },
  };
  CapabilityIdEnum = CapabilityIdEnum;
  scenariosEnabledForAtLeastOneSite = false;
  metricsAndReportsState: any;
  isAlcmeonUser = this.alcmeonContextService.isAlcmeonUser();

  private subscriptions = new Subscription();

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  constructor(
    public dialog: MatDialog,
    private sitesService: SitesService,
    private scenariosService: ScenariosService,
    private searchBarService: SearchBarService,
    private customRouter: CustomRouter,
    private metricsService: MetricsService,
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    let hasLinkToMetrics = false;
    const { isMarketingMode } = customRouter.current.data;
    this.scenarioFlavor = isMarketingMode ? 'campaign-scenarios' : 'quick-replies';
    this.scenariosService.flavor = this.scenarioFlavor;
    this.searchBarService.sort = { active: 'editionDate', direction: 'desc' };
    if (this.scenarioFlavor === 'quick-replies') {
      hasLinkToMetrics = true;
    } else if (this.scenarioFlavor === 'campaign-scenarios') {
      this.searchBarService.sort = { active: 'editionDate', direction: 'desc' };
      topToolbarService.setTitleTranslation('parameters/quick_replies/campaign_title');
    }
    const sitesWithQuickRepliesEnabled = (this.sitesService.sitesV2 || []).filter(
      this.keepSitesWithQuickRepliesEnabled
    );

    this.scenariosEnabledForAtLeastOneSite = sitesWithQuickRepliesEnabled.length > 0;

    if (hasLinkToMetrics) {
      this.scenarioMetricId();
    }

    this.searchBarService.referer$ = this.scenariosService.retrieveQuickReplies;
    this.searchBarService.formatter = (element: Scenario) => {
      const { id, created_by_email, last_modified_by_email, name, site_id, flavor } = element;
      const administratorLabel = this.basicService.translate('admin/users/is_admin');
      element.site = this.sitesService.enrichSiteWithLogo(this.sitesService.sitesV2Map.get(site_id));
      element.createdByEmail = created_by_email || administratorLabel;
      element.lastModifiedByEmail = last_modified_by_email || administratorLabel;
      const scenarioHref = this.basicService.fullURLForRoutePath(
        `/#/${this.alcmeonContextService.companyId}/fuse/parameters/account/${site_id}/${flavor}/${id}`
      );
      element['scenarioNameLink'] = {
        text: name,
        href: scenarioHref,
        newTab: false,
      };
    };

    this.searchBarService.submitQuery();
  }

  keepSitesWithQuickRepliesEnabled = (site: SiteV2) => site.quick_replies_enabled === 1;

  primaryButtonAction = () => {
    this.customRouter.go(`auth.alcmeon.fuse.parameters.all-accounts.${this.scenarioFlavor}.create`);
  };

  duplicate = ({ id }: { id: number }) => {
    this.customRouter.go(`auth.alcmeon.fuse.parameters.all-accounts.${this.scenarioFlavor}.duplicate`, {
      scenarioId: id,
    });
  };

  delete = (scenario: Scenario) => {
    if (!this.isAlcmeonUser) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('parameters/quick_replies/confirm_scenario_deletion'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.basicService.showSpinner();
        this.scenariosService
          .deleteScenario(scenario)
          .pipe(finalize(() => this.basicService.hideSpinner()))
          .subscribe({
            next: () => this.searchBarService.submitQuery(),
          });
      }
    });
  };

  private scenarioMetricId() {
    this.metricsService.getCareMetrics().subscribe({
      next: careMetrics => {
        let result = null;
        if (careMetrics) {
          careMetrics.forEach(category => {
            if (category.metrics) {
              result = category.metrics.find(metric => metric.type === 13)?.id;
            }
          });
        }
        if (result) {
          this.metricsAndReportsState = this.customRouter.href(`auth.alcmeon.fuse.metrics-and-reports.metrics`, {
            metricId: result,
          });
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
    this.topToolbarService.clearTitle();
  }
}
